import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';
import { useEmployeeSearch } from '../hooks/use-lookups';
import { EmployeeOption } from '../types/lookup.types';

export interface EmployeeSearchPickerProps {
  onSelect: (employee: EmployeeOption) => void;
  /** IAM user id shown as picked; leave empty when picking adds to a list (peers, managers). */
  value?: string;
  /** Name to show for `value` while it is not among the current search results. */
  selectedLabel?: string;
  /** IAM user ids that cannot be picked (already chosen, the reviewee themself, ...). */
  excludeIds?: string[];
  placeholder?: string;
  /** Limit the search to one unit (e.g. a head of unit may only pick colleagues of their own unit). */
  departmentId?: string;
  disabled?: boolean;
  id?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

/** Searchable employee dropdown: the caller decides what picking someone means (peer, manager, employee filter). */
export const EmployeeSearchPicker = ({
  onSelect,
  value = '',
  selectedLabel,
  excludeIds = [],
  placeholder,
  departmentId,
  ...triggerProps
}: EmployeeSearchPickerProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const { data: employees = [], isLoading } = useEmployeeSearch(keyword, departmentId);
  const available = employees.filter((employee) => !excludeIds.includes(employee.UserId));
  const options = available.map((employee) => ({
    value: employee.UserId,
    label: employee.DisplayName,
    description: [employee.Designation, employee.DepartmentName, employee.Email]
      .filter(Boolean)
      .join(' · '),
  }));

  return (
    <SearchableSelect
      {...triggerProps}
      value={value}
      options={options}
      onChange={(userId) => {
        const picked = available.find((employee) => employee.UserId === userId);
        if (picked) onSelect(picked);
      }}
      onSearchChange={setInput}
      selectedLabel={selectedLabel}
      placeholder={placeholder ?? t('SELECT_EMPLOYEE')}
      searchPlaceholder={t('SEARCH_EMPLOYEE')}
      emptyText={t('NO_DATA_FOUND')}
      isLoading={isLoading}
    />
  );
};
